import { $http } from '@/common/api/http.service';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { buildAidboxQueryString, extractAidboxQueryData } from '@/composables/useAidboxQuery';
import { useAuthStore } from '@/stores/auth';

/**
 * Account-scoped, and it has to be: security.yaml only puts a JWT firewall in front of
 * `^/api/account/[0-9a-fA-F-]+/` (plus /api/system/ and /api/twilio/). A path outside those reaches
 * the `main` firewall, where `security: false` means the Bearer token this service attaches is
 * never read and the controller sees no authenticated user at all (#5343).
 *
 * The id comes from the store rather than a caller argument for the same reason the server takes
 * the account from the token: it is not the caller's to choose.
 */
const basePath = () => `/api/account/${useAuthStore().account?.id}/messaging`;

export default {
    /**
     * Send one message to a thread.
     *
     * Note there is no `recipient` argument, and adding one would do nothing: the server derives
     * the recipient list from the thread's active participant rows and never reads a recipient
     * field from the payload. That list is what enforces invitation-forward visibility, so it is
     * deliberately not the client's to set (#5343).
     *
     * Reads do NOT go through the server — they go direct to Aidbox, gated by the messaging-*
     * access policies. `getReactions` below is in this module for discoverability but still reads
     * through httpFhir.service for exactly that reason; see its docblock.
     */
    sendMessage: async ({ threadId, body, privateToStaff = false }) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/messages`, { body, privateToStaff });
        return res.data;
    },

    /**
     * Start a standalone internal staff-to-staff thread and post its opening message (#5338).
     *
     * No user JWT holds Create on BN_MessageThread or BN_MessageThreadParticipant, and no access
     * policy could grant it safely — a policy cannot read a request body, so "the thread you are
     * creating must list you as a participant" is not expressible as one. Hence a server endpoint.
     *
     * `topic` is the optional free-text subject line. Not to be confused with `subject`, which is
     * the CLIENT a thread is about — there is deliberately no `subject` argument, because a
     * client-associated thread needs the staff->client chart-access check, which has no PHP
     * implementation yet, so the server refuses one (#5332).
     *
     * @returns {Promise<{ threadId: string }>}
     */
    createThread: async ({ participantIds, body, topic }) => {
        const res = await $http.post(`${basePath()}/threads`, { participantIds, body, topic });
        return res.data;
    },

    /**
     * Thread metadata plus the participant roster.
     *
     * The roster has to come from the server: `messaging-participant-read` lets a browser read only
     * its OWN participant row, so a client cannot enumerate a thread's members. The messages do
     * NOT come from here — see useMessageThread for why they are read straight from Aidbox.
     */
    getThread: async (threadId) => {
        const res = await $http.get(`${basePath()}/threads/${threadId}`);
        return res.data;
    },

    /**
     * Rename a thread, or clear its subject by passing an empty string.
     *
     * Server-side because no user JWT holds Update on BN_MessageThread — a policy cannot express
     * "you may retitle a thread you participate in" without reading its participant rows.
     *
     * @returns {Promise<{ threadId: string, topic: string|null }>}
     */
    updateThreadTopic: async (threadId, topic) => {
        const res = await $http.patch(`${basePath()}/threads/${threadId}`, { topic });
        return res.data;
    },

    /**
     * Credentials for the live message stream (#5338).
     *
     * The gateway URL comes from the server rather than a VITE_ variable: the server already knows it
     * as BN_SOCK_PUBLIC_URL, and deriving it here would be a second source of truth to keep in step
     * across every deploy path — plus a new variable through both firebase-deploy workflows and a
     * GitHub secret.
     *
     * The token is separate from the login JWT because the gateway cannot verify that one — the app
     * signs RS256, and the gateway verifies connect tokens with the HS256 secret it shares with every
     * app that publishes through it.
     *
     * `channel` is informational: the connection carries only the token, and the channel it may join
     * is the one baked into it. Nothing the browser sends chooses a channel.
     *
     * @returns {Promise<{ gatewayUrl: string, channel: string, token: string, expiresIn: number }>}
     */
    getStreamToken: async () => {
        // Silent on failure. This runs unprompted whenever the layout mounts, so the default
        // interceptor would put a "You do not have permission to view this content." snackbar in
        // front of any user whose session has lapsed or who lacks the permission — for a background
        // connection they never asked for. useMessageStream handles the failure itself.
        const res = await $http.get(`${basePath()}/stream-token`, { errorHandle: false, skipLogging: true });
        return res.data;
    },

    /**
     * Add one colleague to an existing thread (#5339).
     *
     * Membership is server-written for the same reason threads are: no user JWT holds Create or
     * Update on BN_MessageThreadParticipant, and a policy cannot read a request body, so "you may
     * add someone to a thread you are in" is not expressible as one.
     *
     * Returns the whole roster, not just the added member — replace the roster with it rather than
     * merging, since the row may have been a reactivation of a previously removed member.
     *
     * @returns {Promise<{ participants: Array<{ practitionerId: string, displayName: string, active: boolean, joinedAt: string|null }> }>}
     */
    inviteParticipant: async ({ threadId, practitionerId }) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/participants`, { practitionerId });
        return res.data;
    },

    /**
     * The colleagues this user may message, for both staff pickers (#5374).
     *
     * Server-side because the deciding fact is unreachable from here: `message_with_other_users`
     * lives in account `Role` rows, and API Platform exposes `Role` only under `MANAGE_ROLES`. The
     * Aidbox `User` resource carries `active` and `practitioner` but no permissions, so no
     * combination of FHIR searches can answer "may this person take part in a staff conversation".
     *
     * This replaces the brand PractitionerRole + `User` search pair each dialog used to run. The
     * server applies the same restrictions those did — brand scope, an active linked `User`, and the
     * shared-location clause for a restricted caller — so nothing here needs to re-apply them.
     *
     * @returns {Promise<Array<{ practitionerId: string, displayName: string }>>} sorted by name
     */
    getColleagues: async (brandId) => {
        const res = await $http.get(`${basePath()}/colleagues`, { params: { brand: brandId } });
        const colleagues = res.data?.colleagues;
        // A 200 without a `colleagues` array is a shape surprise, and `?? []` would have turned it into
        // an empty picker — indistinguishable from "nobody is eligible", which is now a normal outcome.
        // Thrown so the caller shows its load error instead of a silently empty list.
        if (!Array.isArray(colleagues)) {
            throw new Error('The colleague list response carried no colleagues array.');
        }
        return colleagues;
    },

    /**
     * Mark a conversation read for the signed-in user (#5376).
     *
     * The write itself is one field on the caller's own participant row, and this endpoint is the
     * only way it happens: no policy grants a browser a write on a participant row, because a
     * policy cannot read a request body and so could not hold the write down to lastReadAt. It also
     * has to ANNOUNCE — so the sender's read receipts update without a reload — and only the server
     * can publish to another user's Mercure topic. There is no `lastReadAt` argument: the timestamp is the server's,
     * not the caller's, and no participant row id either, because it is not the caller's to choose.
     *
     * @returns {Promise<{ threadId: string, lastReadAt: string }>}
     */
    markThreadRead: async (threadId) => {
        const res = await $http.post(`${basePath()}/threads/${threadId}/read`, {});
        return res.data;
    },

    /**
     * Add or withdraw the caller's reaction to one message (#5376).
     *
     * Server-side because a policy cannot join a reaction to the reacted-to message's recipient
     * list, which is what decides who may react at all. Note there is no `practitionerId` argument:
     * whose reaction it is, is identity, and identity is resolved server-side.
     *
     * `remove` picks the verb. Withdrawing deactivates the caller's own row — nothing
     * messaging-related is deleted, reactions included.
     *
     * Returns the whole reaction set for the message, not just the change: replace rather than
     * merge, since the write may have reactivated a row that was already there.
     *
     * @returns {Promise<{ messageId: string, reactions: Array<{ emoji: string, practitionerId: string, displayName: string }> }>}
     */
    toggleReaction: async ({ messageId, emoji, remove = false }) => {
        const url = `${basePath()}/messages/${messageId}/reactions`;
        const res = remove ? await $http.delete(url, { data: { emoji } }) : await $http.post(url, { emoji });
        return res.data;
    },

    /**
     * Rewrite the body of a message the caller sent (#5376).
     *
     * PATCH, never PUT: a whole-resource replace would let a caller submit `recipient` alongside
     * the new text, and that list is frozen at send time and IS the read gate. The server's patch
     * names exactly `payload` and `editedAt`.
     *
     * @returns {Promise<{ id: string, body: string, editedAt: string }>}
     */
    editMessage: async ({ messageId, body }) => {
        const res = await $http.patch(`${basePath()}/messages/${messageId}`, { body });
        return res.data;
    },

    /**
     * Every standing reaction in one conversation (#5376).
     *
     * The one READ in this module, and it deliberately does not go through the server API: like the
     * messages themselves, reactions must be read under the caller's own JWT so Aidbox decides what
     * they may see. `AidboxQuery/message-reactions` anchors every row on the reacted-to message's
     * frozen `recipient` list containing `{{user.practitioner.id}}`, so a message the caller cannot
     * read contributes no reactions — the same predicate that gives invitation-forward visibility.
     * Reading these through the API instead would authenticate as the unrestricted `api-platform`
     * client and leak the existence of messages the caller was never sent.
     *
     * One query per conversation rather than one per message, so a long thread is not an N+1.
     *
     * @returns {Promise<Array<{ messageId: string, emoji: string, practitionerId: string, displayName: string }>>}
     */
    getReactions: async (threadId) => {
        const qs = buildAidboxQueryString({ thread: threadId, _limit: 1000, _offset: 0 });
        const result = await $httpFhirApi.get(`/$query/message-reactions?${qs}`);
        return extractAidboxQueryData(result).map((row) => ({
            messageId: row.message_id,
            emoji: row.emoji,
            practitionerId: row.practitioner_id,
            // Falls back to the id rather than blank, as the roster does.
            displayName: row.display_name || row.practitioner_id,
        }));
    },
};
